import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { SKIP_GLOBAL_ERROR_HANDLING } from 'src/app/core/constants/http-context-tokens';
import { IngestorTenantMap } from 'src/app/features/admin/organizations/components/integrations/services/ingestor.service';

/** What the add/edit dialog sends. `id` set → single PUT; unset → bulk PUT with one item. */
export interface IngestorTenantMapWrite {
  id?: number;
  organization: number;
  integration_id: string;
  integration_unique_name: string | null;
}

export interface IngestorTenantMapListResponse {
  '@odata.count': number;
  value: IngestorTenantMap[];
}

export interface CsvImportRowError {
  row: number;
  organization_id: number | null;
  message: string;
}

/** POST .../csv/ answer. 200 on success, the same body with 400 when nothing was written. */
export interface CsvImportResult {
  created: number;
  updated: number;
  unchanged: number;
  skipped_blank: number;
  errors: CsvImportRowError[];
}

/** field → messages. `detail` carries non-field errors. */
export type TenantMapFieldErrors = Record<string, string[]>;

/** Same rule as tenant_csv_headers on the backend: trailing " ID" becomes " Name". */
export function tenantNameLabel(idLabel: string): string {
  const label = idLabel.trim() || 'External ID';
  return /\sid$/i.test(label) ? label.replace(/\sid$/i, ' Name') : `${label} Name`;
}

function toMessages(value: unknown): string[] {
  if (typeof value === 'string') {
    return value.trim() ? [value] : [];
  }
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string' && item.trim() !== '');
  }
  return [];
}

/**
 * Single PUT answers {field: [..]}. Bulk PUT answers {items: [{}, {field: [..]}]}, index-aligned.
 * We only ever send one item, but merge every index anyway so nothing gets lost.
 */
export function extractTenantMapFieldErrors(error: unknown): TenantMapFieldErrors {
  const body = (error as { error?: unknown } | null | undefined)?.error;
  if (typeof body === 'string') {
    return body.trim() ? { detail: [body] } : {};
  }
  if (!body || typeof body !== 'object') {
    return {};
  }

  const record = body as Record<string, unknown>;
  const items = record['items'];
  const sources: Record<string, unknown>[] = Array.isArray(items)
    ? items.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
    : [record];

  const out: TenantMapFieldErrors = {};
  for (const source of sources) {
    for (const [field, value] of Object.entries(source)) {
      const messages = toMessages(value);
      if (messages.length) {
        out[field] = [...(out[field] ?? []), ...messages];
      }
    }
  }
  return out;
}

export function isCsvImportResult(body: unknown): body is CsvImportResult {
  if (!body || typeof body !== 'object') {
    return false;
  }
  const candidate = body as Record<string, unknown>;
  const counters = ['created', 'updated', 'unchanged', 'skipped_blank'];
  return counters.every((key) => typeof candidate[key] === 'number') && Array.isArray(candidate['errors']);
}

/** Client-side name. Content-Disposition is not readable cross-origin, so we do not try. */
export function csvFilename(integrationName: string, template: boolean): string {
  const slug =
    integrationName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'integration';
  return `${slug}-tenant-mappings${template ? '-template' : ''}.csv`;
}

/** Anchor trick from inventory.service.ts. The browser owns the file after click(). */
export function saveBlobAsFile(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

// providedIn null: the mapping component provides it, one instance per open screen (same as TenantMappingService).
@Injectable({ providedIn: null })
export class IngestorTenantMapService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/v1/ingestor/ingestor`;

  /** No query string here. The datatable appends its own `?$skip=...`. */
  listUrl(recordId: number): string {
    return `${this.base}/ingestortenantmaps(${recordId})/`;
  }

  /** Both answers are ignored; the caller reloads the table. Bulk PUT is 204, single PUT echoes the row. */
  upsert(recordId: number, mapping: IngestorTenantMapWrite): Observable<void> {
    if (mapping.id) {
      return this.http.put<void>(`${this.base}/ingestortenantmap(${mapping.id})/`, mapping, {
        context: this.callerHandlesErrors(),
      });
    }
    return this.http.put<void>(this.listUrl(recordId), { items: [mapping] }, { context: this.callerHandlesErrors() });
  }

  delete(pk: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/ingestortenantmap(${pk})/`, { context: this.callerHandlesErrors() });
  }

  downloadCsv(recordId: number, template: boolean): Observable<Blob> {
    const params = template ? new HttpParams().set('template', 'true') : new HttpParams();
    return this.http.get(`${this.listUrl(recordId)}csv/`, {
      params,
      responseType: 'blob',
      context: this.callerHandlesErrors(),
    });
  }

  uploadCsv(recordId: number, file: File, dryRun: boolean): Observable<CsvImportResult> {
    const body = new FormData();
    // Passing a filename here makes Chrome copy the File. The name is already on it.
    body.append('file', file);
    const params = dryRun ? new HttpParams().set('dry_run', 'true') : new HttpParams();
    return this.http.post<CsvImportResult>(`${this.listUrl(recordId)}csv/`, body, {
      params,
      context: this.callerHandlesErrors(),
    });
  }

  // The global interceptor turns errors into a plain Error; callers need the raw 400 to show field and row errors.
  // A failed blob download would also toast an empty message there, since a Blob body has no keys to flatten.
  private callerHandlesErrors(): HttpContext {
    return new HttpContext().set(SKIP_GLOBAL_ERROR_HANDLING, true);
  }
}
